import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDemoData } from '../components/useDemoData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Presentation, Download, TrendingUp, TrendingDown, DollarSign,
  BarChart3, PieChart, Users, AlertTriangle, CheckCircle2,
  Calendar, ArrowRight, Zap, Target, FileText, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import EmptyDataState from '../components/EmptyDataState';

const DEMO_DECK = {
  meta: { quarter: 'Q1 2026', generatedAt: 'Apr 20, 2026', pages: 8 },
  executiveSummary: {
    totalSpend: 892400,
    prevQuarterSpend: 814200,
    changePercent: 9.6,
    totalSaved: 147800,
    savingsTarget: 180000,
    savingsProgress: 82,
    forecastNextQ: 945000,
    wasteEliminated: 18,
    costPerEmployee: 742,
    cloudAsRevPercent: 8.4,
  },
  byBU: [
    { name: 'Engineering', spend: 412000, budget: 420000, variance: -1.9, trend: 'stable' },
    { name: 'Data & ML', spend: 198000, budget: 160000, variance: 23.8, trend: 'up' },
    { name: 'Platform & Infra', spend: 156000, budget: 165000, variance: -5.5, trend: 'down' },
    { name: 'Product', spend: 78400, budget: 80000, variance: -2.0, trend: 'stable' },
    { name: 'Security & Compliance', spend: 48000, budget: 50000, variance: -4.0, trend: 'stable' },
  ],
  byProvider: [
    { name: 'AWS', spend: 534000, percent: 59.8, change: 7.2 },
    { name: 'Azure', spend: 198000, percent: 22.2, change: 14.1 },
    { name: 'GCP', spend: 112400, percent: 12.6, change: 8.9 },
    { name: 'Snowflake', spend: 48000, percent: 5.4, change: 22.0 },
  ],
  keyWins: [
    { title: 'Right-sized 84 EC2 instances', savings: 42000, team: 'Platform' },
    { title: 'Eliminated 23 zombie resources', savings: 31400, team: 'Engineering' },
    { title: 'Negotiated Snowflake contract renewal', savings: 28800, team: 'Data & ML' },
    { title: 'Migrated 12 workloads to Graviton', savings: 24600, team: 'Platform' },
    { title: 'Consolidated 8 duplicate SaaS tools', savings: 21000, team: 'IT' },
  ],
  risks: [
    { risk: 'Data & ML team 24% over budget — GPU costs growing faster than forecast', severity: 'high', mitigation: 'Implement spot instances for training; review inference costs' },
    { risk: 'Snowflake costs up 22% QoQ — warehouse auto-scaling unbounded', severity: 'medium', mitigation: 'Set warehouse size caps; implement query governance' },
    { risk: 'Azure spend growing 14% — new team onboarding without cost guardrails', severity: 'medium', mitigation: 'Deploy budget alerts and guardrails for new Azure subscriptions' },
  ],
  forecast: {
    nextQ: 945000,
    nextQRange: [890000, 1020000],
    yearEnd: 3780000,
    yearEndRange: [3540000, 4100000],
    keyDrivers: ['AI/ML compute expansion (+$52K)', 'New Azure workloads (+$28K)', 'Offset by commitment savings (-$34K)'],
  },
  recommendation: 'Recommend board approve $180K optimization budget for Q2 to target $400K annual savings through reserved instance purchases, spot instance migration, and vendor contract renegotiations.',
};

const BoardDeckGenerator = () => {
  const { shouldShowData } = useDemoData();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [deck, setDeck] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    if (shouldShowData) { setTimeout(() => setLoading(false), 500); }
    else { setLoading(false); }
  }, [shouldShowData]);

  const handleGenerate = async () => {
    // Iter84ak — kick off real backend generation; UI uses local rich demo
    // for the slide preview while the backend job renders the PDF.
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/finops/board-deck/generate`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
    } catch (_e) { /* fall through to local demo */ }
    setTimeout(() => { setDeck(DEMO_DECK); setGenerating(false); toast.success('Board deck generated!'); }, 2500);
  };

  if (!shouldShowData) {
    return <EmptyDataState title="Board Deck Generator" description="Auto-generate CFO-ready quarterly board presentations from your cloud cost data. One click, 30 seconds, investor-grade quality." icon={Presentation} />;
  }

  if (loading) return <div className="p-6 flex items-center justify-center min-h-[400px]"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  const slides = ['Executive Summary', 'Spend by Business Unit', 'Provider Breakdown', 'Key Wins', 'Risks & Mitigations', 'Forecast', 'Recommendations'];

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="board-deck-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 flex items-center gap-2">
            <Presentation className="w-7 h-7 text-emerald-400" />
            Board Deck <span className="text-emerald-400">Generator</span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">CFO-ready quarterly presentations in 30 seconds. Not days.</p>
        </div>
        {!deck ? (
          <Button className="bg-emerald-500 hover:bg-indigo-600" onClick={handleGenerate} disabled={generating} data-testid="generate-deck-btn">
            {generating ? <><Sparkles className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><Sparkles className="w-4 h-4 mr-2" />Generate Q1 2026 Deck</>}
          </Button>
        ) : (
          <Button className="bg-emerald-500 hover:bg-emerald-600" onClick={() => toast.success('Deck downloaded as PDF!')} data-testid="download-deck-btn">
            <Download className="w-4 h-4 mr-2" />Export as PDF
          </Button>
        )}
      </div>

      {!deck ? (
        <Card className="border-2 border-dashed border-slate-200">
          <CardContent className="p-16 text-center">
            {generating ? (
              <div className="space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center animate-pulse"><Presentation className="w-8 h-8 text-indigo-500" /></div>
                <h3 className="text-lg font-semibold text-slate-700">Building your board deck...</h3>
                <Progress value={65} className="max-w-xs mx-auto" />
                <p className="text-sm text-slate-400">Analyzing spend data, generating narratives, creating charts...</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center"><Presentation className="w-8 h-8 text-slate-400" /></div>
                <h3 className="text-lg font-semibold text-slate-700">Generate your quarterly board deck</h3>
                <p className="text-sm text-slate-400">AI analyzes your cloud data and creates a complete presentation with exec summary, trends, risks, and recommendations.</p>
                <div className="flex flex-wrap justify-center gap-2 mt-4">
                  {slides.map((s, i) => <Badge key={i} variant="outline" className="text-xs">{s}</Badge>)}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-[220px_1fr] gap-6">
          {/* Slide Navigation */}
          <div className="space-y-1">
            <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-3 px-2">Slides</p>
            {slides.map((s, i) => (
              <button key={i} onClick={() => setActiveSlide(i)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${activeSlide === i ? 'bg-emerald-50 text-emerald-700 font-medium border border-emerald-200' : 'text-slate-400 hover:bg-slate-50'}`}>
                <span className="text-xs text-slate-400 mr-2">{i + 1}.</span>{s}
              </button>
            ))}
          </div>

          {/* Slide Content */}
          <Card className="min-h-[500px]">
            <CardContent className="p-6">
              {activeSlide === 0 && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between"><h2 className="text-xl font-bold text-slate-800">Executive Summary — {deck.meta.quarter}</h2><Badge className="bg-emerald-500/10 text-emerald-600 border-indigo-500/30">{deck.meta.generatedAt}</Badge></div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-slate-50 rounded-xl p-4"><p className="text-xs text-slate-400">Total Cloud Spend</p><p className="text-2xl font-bold text-slate-800">${(deck.executiveSummary.totalSpend / 1000).toFixed(0)}K</p><p className={`text-xs mt-1 ${deck.executiveSummary.changePercent > 0 ? 'text-red-500' : 'text-emerald-500'}`}>+{deck.executiveSummary.changePercent}% QoQ</p></div>
                    <div className="bg-emerald-50 rounded-xl p-4"><p className="text-xs text-emerald-600">Total Saved</p><p className="text-2xl font-bold text-emerald-700">${(deck.executiveSummary.totalSaved / 1000).toFixed(0)}K</p><p className="text-xs mt-1 text-emerald-500">{deck.executiveSummary.savingsProgress}% of target</p></div>
                    <div className="bg-slate-50 rounded-xl p-4"><p className="text-xs text-slate-400">Cost/Employee</p><p className="text-2xl font-bold text-slate-800">${deck.executiveSummary.costPerEmployee}</p><p className="text-xs mt-1 text-slate-400">per month</p></div>
                    <div className="bg-slate-50 rounded-xl p-4"><p className="text-xs text-slate-400">Cloud as % Revenue</p><p className="text-2xl font-bold text-slate-800">{deck.executiveSummary.cloudAsRevPercent}%</p><p className="text-xs mt-1 text-slate-400">industry avg: 10-12%</p></div>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-xl border border-blue-100"><p className="text-sm text-blue-800 font-medium">Key Insight</p><p className="text-sm text-emerald-600 mt-1">Cloud spend grew 9.6% QoQ, driven primarily by AI/ML workloads. However, optimization efforts offset $148K, keeping effective growth below forecast. Waste reduced from 22% to 18% of total spend.</p></div>
                </div>
              )}
              {activeSlide === 1 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-slate-800">Spend by Business Unit</h2>
                  <div className="space-y-4">
                    {deck.byBU.map((bu, i) => (
                      <div key={i} className={`p-4 rounded-xl border ${bu.variance > 10 ? 'bg-red-50 border-red-200' : 'bg-white border-slate-100'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div><p className="font-semibold text-slate-800">{bu.name}</p><p className="text-xs text-slate-400">Budget: ${(bu.budget / 1000).toFixed(0)}K</p></div>
                          <div className="text-right"><p className="text-lg font-bold text-slate-800">${(bu.spend / 1000).toFixed(0)}K</p><p className={`text-xs font-medium ${bu.variance > 0 ? 'text-red-500' : 'text-emerald-500'}`}>{bu.variance > 0 ? '+' : ''}{bu.variance}% vs budget</p></div>
                        </div>
                        <Progress value={Math.min(100, (bu.spend / bu.budget) * 100)} className="h-2" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {activeSlide === 2 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-slate-800">Provider Breakdown</h2>
                  <div className="grid grid-cols-2 gap-4">
                    {deck.byProvider.map((p, i) => (
                      <div key={i} className="p-5 rounded-xl bg-slate-50 border border-slate-100">
                        <div className="flex items-center justify-between mb-3"><span className="font-bold text-slate-800 text-lg">{p.name}</span><Badge variant="outline">{p.percent}%</Badge></div>
                        <p className="text-2xl font-bold text-slate-900">${(p.spend / 1000).toFixed(0)}K</p>
                        <p className={`text-xs mt-1 ${p.change > 15 ? 'text-red-500' : p.change > 10 ? 'text-amber-500' : 'text-slate-400'}`}>+{p.change}% QoQ</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {activeSlide === 3 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-slate-800">Key Wins This Quarter</h2>
                  <div className="space-y-3">
                    {deck.keyWins.map((w, i) => (
                      <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                        <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0" />
                        <div className="flex-1"><p className="font-medium text-slate-800">{w.title}</p><p className="text-xs text-slate-400">{w.team}</p></div>
                        <p className="text-lg font-bold text-emerald-600">${(w.savings / 1000).toFixed(1)}K saved</p>
                      </div>
                    ))}
                    <div className="p-4 bg-emerald-100 rounded-xl text-center"><p className="text-lg font-bold text-emerald-800">Total Q1 Savings: ${(deck.executiveSummary.totalSaved / 1000).toFixed(0)}K</p></div>
                  </div>
                </div>
              )}
              {activeSlide === 4 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-slate-800">Risks & Mitigations</h2>
                  <div className="space-y-3">
                    {deck.risks.map((r, i) => (
                      <div key={i} className={`p-4 rounded-xl border ${r.severity === 'high' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                        <div className="flex items-start gap-3">
                          <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${r.severity === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
                          <div><p className="font-medium text-slate-800">{r.risk}</p><p className="text-sm text-slate-400 mt-2"><span className="font-medium">Mitigation:</span> {r.mitigation}</p></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {activeSlide === 5 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-slate-800">Q2 2026 Forecast</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-5 rounded-xl bg-emerald-50 border border-blue-100"><p className="text-xs text-emerald-600">Next Quarter Estimate</p><p className="text-3xl font-bold text-blue-800">${(deck.forecast.nextQ / 1000).toFixed(0)}K</p><p className="text-xs text-emerald-500">Range: ${(deck.forecast.nextQRange[0] / 1000).toFixed(0)}K - ${(deck.forecast.nextQRange[1] / 1000).toFixed(0)}K</p></div>
                    <div className="p-5 rounded-xl bg-slate-50 border border-slate-100"><p className="text-xs text-slate-400">Full Year Projection</p><p className="text-3xl font-bold text-slate-800">${(deck.forecast.yearEnd / 1000).toFixed(0)}K</p><p className="text-xs text-slate-400">Range: ${(deck.forecast.yearEndRange[0] / 1000).toFixed(0)}K - ${(deck.forecast.yearEndRange[1] / 1000).toFixed(0)}K</p></div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl"><p className="text-sm font-medium text-slate-700 mb-2">Key Forecast Drivers:</p><ul className="space-y-1">{deck.forecast.keyDrivers.map((d, i) => <li key={i} className="text-sm text-slate-400 flex items-center gap-2"><ArrowRight className="w-3 h-3 text-slate-400" />{d}</li>)}</ul></div>
                </div>
              )}
              {activeSlide === 6 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-slate-800">Board Recommendation</h2>
                  <div className="p-6 bg-emerald-50 rounded-xl border border-emerald-200"><p className="text-base text-indigo-800 leading-relaxed">{deck.recommendation}</p></div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl bg-slate-50 text-center"><p className="text-xs text-slate-400">Investment Ask</p><p className="text-2xl font-bold text-slate-800">$180K</p></div>
                    <div className="p-4 rounded-xl bg-emerald-50 text-center"><p className="text-xs text-emerald-600">Projected Return</p><p className="text-2xl font-bold text-emerald-700">$400K</p></div>
                    <div className="p-4 rounded-xl bg-emerald-50 text-center"><p className="text-xs text-emerald-600">ROI</p><p className="text-2xl font-bold text-emerald-700">2.2x</p></div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BoardDeckGenerator;
